import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export const queryKeys = {
  app: ["app"] as const,
  session: ["auth", "session"] as const,
  workQueue: ["operator", "work-queue"] as const,
  actionStack: ["operator", "action-stack"] as const,
  automationRail: ["operator", "automation", "scheduled"] as const,
  reportingDeliveries: (limit: number) => ["operator", "reporting", "deliveries", limit] as const,
  reportingRecovery: (windowDays: number) =>
    ["operator", "reporting", "recovery", windowDays] as const,
  notifications: ["operator", "notifications"] as const,
  callbackSla: ["operator", "callback-sla"] as const,
  paymentReminders: ["operator", "payment-reminders"] as const,
  winbackCandidates: ["operator", "winback-candidates"] as const,
  appointmentReminders: ["operator", "appointment-reminders"] as const,
  expiredEstimates: ["operator", "expired-estimates"] as const,
  agingReport: ["operator", "aging-report"] as const,
  revenueProof: ["operator", "revenue-proof"] as const,
  outcomeSummary: (days: number) => ["outcomes", "summary", days] as const,
  touchEffectiveness: (groupBy: string, days: number) =>
    ["analytics", "touch-effectiveness", groupBy, days] as const,
  accountMembers: ["account", "members"] as const,
  accountSubscription: ["account", "subscription"] as const,
  accountAuditTrail: (
    eventType: string | undefined,
    limit: number,
    phoneKey: string,
  ) =>
    ["account", "audit-trail", eventType ?? "_all", limit, phoneKey] as const,
  selfSessions: ["auth", "sessions"] as const,
  callIntelligenceRecent: (limit: number, leadId: string | null) =>
    ["operator", "call-intelligence", "recent", limit, leadId] as const,
  contactInteractions: (phoneKey: string | null, limit: number) =>
    ["operator", "contacts", phoneKey, "interactions", limit] as const,
  contactHistory: (phoneKey: string | null, limit: number) =>
    ["operator", "contacts", phoneKey, "history", limit] as const,
  importSources: ["account", "imports", "sources"] as const,
  importRuns: (limit: number) => ["account", "imports", "runs", limit] as const,
} as const;

/* ── Queries ── */

export function useAppState() {
  return useQuery({
    queryKey: queryKeys.app,
    queryFn: () => api<unknown>("/api/app"),
  });
}

export function useSession() {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: () => api<unknown>("/api/auth/session"),
  });
}

export function useWorkQueue() {
  return useQuery({
    queryKey: queryKeys.workQueue,
    queryFn: () => api<unknown>("/api/operator/work-queue"),
  });
}

export interface ActionStackCallContext {
  intent: string | null;
  urgency: string | null;
  equipmentType: string | null;
  sentiment: string | null;
  serviceWindowSignal: string | null;
  objections: string[];
  capturedAt: string;
}

export type PipelineStage =
  | "missed"
  | "qualified"
  | "quoted"
  | "negotiating"
  | "declined"
  | "won";

export interface CoalescedSignal {
  id: string;
  type: string;
  reason: string;
  recommendedAction: string;
  dollarValue: number;
  valueLabel: string;
  pipelineStage: PipelineStage;
}

export type PreferredChannelValue = "call" | "sms" | "email" | "";
export type PreferredChannelSource = "explicit" | "derived" | "none";

export interface ContactSignals {
  preferredChannel: PreferredChannelValue;
  preferredChannelSource: PreferredChannelSource;
  smsOptOut: boolean;
  emailOptOut: boolean;
  dncFlag: boolean;
}

export interface ActionStackItem {
  id: string;
  priority: number;
  reason: string;
  recommendedAction: string;
  type: string;
  customerName: string;
  phone: string;
  email: string;
  summary: string;
  valueLabel: string;
  dollarValue: number;
  timeAgo: string;
  jobId: string;
  stage: string;
  source: string;
  callContext: ActionStackCallContext | null;
  recommendedActionReason: string;
  pipelineStage: PipelineStage;
  coalescedSignals: CoalescedSignal[];
  contactSignals: ContactSignals | null;
  voicemailTranscript: string | null;
}

export interface ActionStackResponse {
  items: ActionStackItem[];
  totalItems: number;
  generatedAt: string;
}

export function useActionStack() {
  return useQuery({
    queryKey: queryKeys.actionStack,
    queryFn: () => api<ActionStackResponse>("/api/operator/action-stack"),
  });
}

/**
 * Sign out — invalidates the server session and clears the session cookie.
 * The page reloads through /login (api.ts redirects on 401, but we hard-
 * navigate so component state doesn't briefly render with stale data).
 */
export function useLogout() {
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
    onSuccess: () => {
      window.location.assign("/login");
    },
  });
}

/**
 * Go-live mutation — flips the active account from sandbox to live, wipes
 * the seed data, and stamps went_live_at. Owner-only at the API layer;
 * the UI guards the action behind a confirmation step.
 */
export function useGoLive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean; alreadyLive: boolean; wentLiveAt: string | null }>(
        "/api/operator/account/go-live",
        { method: "POST" },
      ),
    onSuccess: () => {
      // Wipe every cached query — the data they hold no longer exists.
      void qc.invalidateQueries();
    },
  });
}

export type ActionStackOutcome = "won" | "lost" | "callback" | "try_again";

export type OneOffChannel = "sms" | "email";

export function useSendOneOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      itemId: string;
      channel: OneOffChannel;
      body: string;
      to?: string;
      /**
       * The original suggestion body the operator started from, when one
       * was pulled in via ✨ Suggest. Used server-side to log the
       * suggested-vs-final-sent diff as the training signal.
       */
      suggestedBody?: string;
      /**
       * Set by the drawer when the contact has no consent record on file
       * (contactSignals === null) and the operator explicitly affirmed
       * "caller initiated this contact". Logged server-side as a
       * consent.override engagement event so the audit trail shows
       * exactly which operator authorized which outbound under what
       * pretext.
       */
      consentOverride?: boolean;
    }) =>
      api<{ ok: boolean; dispatchId: string; status: string; error?: string }>(
        "/api/operator/action-stack/send",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.actionStack });
      void qc.invalidateQueries({ queryKey: queryKeys.workQueue });
      void qc.invalidateQueries({ queryKey: queryKeys.automationRail });
      void qc.invalidateQueries({
        queryKey: ["operator", "reporting", "recovery"],
      });
    },
  });
}

export interface ComposeSuggestion {
  suggestedBody: string;
  source: "llm" | "fallback";
  modelId: string | null;
}

/**
 * Generate an SMS / email body suggestion for an action-stack item.
 * On-demand: triggered by the ✨ Suggest button in the drawer's
 * compose section. Returns a fallback when the LLM is unavailable so
 * the button never throws to the operator.
 */
export function useComposeSuggest() {
  return useMutation({
    mutationFn: (payload: { itemId: string; channel: OneOffChannel }) =>
      api<ComposeSuggestion>("/api/operator/action-stack/suggest", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
  });
}

export interface AutomationRailItem {
  id: string;
  kind: "cadence_touch" | "dispatch_queued" | "recently_fired";
  channel: string;
  customerName: string;
  label: string;
  reason: string;
  to: string;
  scheduledAt: string;
  scheduledLabel: string;
  targetType: string;
  targetId: string;
  paused: boolean;
  firedAt?: string;
}

export interface AutomationRailResponse {
  items: AutomationRailItem[];
  totalItems: number;
  recentlyFiredCount?: number;
  windowHours: number;
  generatedAt: string;
}

export function useAutomationRail() {
  return useQuery({
    queryKey: queryKeys.automationRail,
    queryFn: () =>
      api<AutomationRailResponse>("/api/operator/automation/scheduled"),
  });
}

export interface ContactInteraction {
  id: string;
  interactionType: string;
  direction: string;
  occurredAt: string;
  subject: string;
  body: string;
}

export interface ContactInteractionsResponse {
  phoneKey: string;
  interactions: ContactInteraction[];
  total: number;
}

/**
 * Last N customer interactions (SMS / email / call) for the lead's phone
 * key. Drives the drawer's "Recent thread" block. The endpoint is the
 * already-existing `/api/operator/contacts/:phoneKey/interactions` — no new
 * server route. Disabled when phoneKey is null so the drawer can render
 * empty without a failing fetch.
 */
export function useContactInteractions(phoneKey: string | null, limit = 5) {
  return useQuery({
    queryKey: queryKeys.contactInteractions(phoneKey, limit),
    enabled: Boolean(phoneKey),
    queryFn: () =>
      api<ContactInteractionsResponse>(
        `/api/operator/contacts/${encodeURIComponent(phoneKey!)}/interactions?limit=${limit}`,
      ),
  });
}

export function useRecordActionStackOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      itemId: string;
      outcome: ActionStackOutcome;
      notes?: string;
    }) =>
      api<{ ok: boolean; itemId: string; outcome: ActionStackOutcome }>(
        "/api/operator/action-stack/outcome",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.actionStack });
      void qc.invalidateQueries({ queryKey: queryKeys.workQueue });
      void qc.invalidateQueries({ queryKey: queryKeys.automationRail });
      void qc.invalidateQueries({
        queryKey: ["operator", "reporting", "recovery"],
      });
    },
  });
}

export function useUndoActionStackOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      api<{ ok: boolean; itemId: string; removed: boolean }>(
        "/api/operator/action-stack/outcome",
        { method: "DELETE", body: JSON.stringify({ itemId }) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.actionStack });
      void qc.invalidateQueries({ queryKey: queryKeys.workQueue });
      void qc.invalidateQueries({ queryKey: queryKeys.automationRail });
      void qc.invalidateQueries({
        queryKey: ["operator", "reporting", "recovery"],
      });
    },
  });
}

export function useCallbackSla() {
  return useQuery({
    queryKey: queryKeys.callbackSla,
    queryFn: () => api<unknown>("/api/operator/callback-sla"),
  });
}

export function usePaymentReminders() {
  return useQuery({
    queryKey: queryKeys.paymentReminders,
    queryFn: () => api<unknown>("/api/operator/payment-reminders"),
  });
}

export function useWinbackCandidates() {
  return useQuery({
    queryKey: queryKeys.winbackCandidates,
    queryFn: () => api<unknown>("/api/operator/winback-candidates"),
  });
}

export function useAppointmentReminders() {
  return useQuery({
    queryKey: queryKeys.appointmentReminders,
    queryFn: () => api<unknown>("/api/operator/appointment-reminders"),
  });
}

export function useExpiredEstimates() {
  return useQuery({
    queryKey: queryKeys.expiredEstimates,
    queryFn: () => api<unknown>("/api/operator/expired-estimates"),
  });
}

export function useAgingReport() {
  return useQuery({
    queryKey: queryKeys.agingReport,
    queryFn: () => api<unknown>("/api/operator/aging-report"),
  });
}

export function useRevenueProof() {
  return useQuery({
    queryKey: queryKeys.revenueProof,
    queryFn: () => api<unknown>("/api/operator/revenue-proof"),
  });
}

export function useOutcomeSummary(days: number) {
  const since = new Date(Date.now() - days * 86_400_000).toISOString();
  const until = new Date().toISOString();
  return useQuery({
    queryKey: queryKeys.outcomeSummary(days),
    queryFn: () =>
      api<unknown>(
        `/api/outcomes/summary?since=${encodeURIComponent(since)}&until=${encodeURIComponent(until)}`,
      ),
  });
}

export function useTouchEffectiveness(groupBy: string, days: number) {
  const since = new Date(Date.now() - days * 86_400_000).toISOString();
  const until = new Date().toISOString();
  return useQuery({
    queryKey: queryKeys.touchEffectiveness(groupBy, days),
    queryFn: () =>
      api<unknown>(
        `/api/analytics/touch-effectiveness?group_by=${encodeURIComponent(groupBy)}&since=${encodeURIComponent(since)}&until=${encodeURIComponent(until)}`,
      ),
  });
}

export interface CallIntelligenceRow {
  id: string;
  recordingSid: string;
  callSid: string | null;
  leadId: string | null;
  transcriptText: string | null;
  recordingDurationSeconds: number | null;
  intent: string | null;
  urgency: string | null;
  equipmentType: string | null;
  objections: string[];
  callerSentiment: string | null;
  serviceWindowSignal: string | null;
  modelId: string | null;
  promptVersion: string | null;
  latencyMs: number | null;
  costUsd: number | null;
  extractionStatus: "pending" | "succeeded" | "failed" | "skipped_no_transcript";
  errorMessage: string | null;
  attempts: number;
  operatorFlag: "accurate" | "partially_correct" | "wrong" | null;
  operatorFlagAt: string | null;
  operatorFlagUserId: string | null;
  operatorFlagNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useCallIntelligenceRecent(
  limit: number = 20,
  leadId: string | null = null,
) {
  return useQuery({
    queryKey: queryKeys.callIntelligenceRecent(limit, leadId),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (leadId) params.set("leadId", leadId);
      return api<{ rows: CallIntelligenceRow[] }>(
        `/api/operator/call-intelligence/recent?${params.toString()}`,
      );
    },
  });
}

/* ── Mutations ── */

export function useJobAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api<unknown>("/api/jobs/action", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
    },
  });
}

export function useWorkflowAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api<unknown>("/api/actions", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
    },
  });
}

export function useBillingInquiry() {
  return useMutation({
    mutationFn: (notes: string) =>
      api<{ ok: boolean }>("/api/billing/inquiry", {
        method: "POST",
        body: JSON.stringify({ notes }),
      }),
  });
}

export type DismissalReason =
  | "bad_number"
  | "wrong_customer"
  | "already_handled"
  | "not_a_fit"
  | "other";

export function useDismissWorkQueueItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { itemId: string; reason?: DismissalReason }) =>
      api<{ ok: boolean; itemId: string; reason: string }>(
        "/api/operator/work-queue/dismiss",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.workQueue });
      void qc.invalidateQueries({ queryKey: queryKeys.actionStack });
      void qc.invalidateQueries({ queryKey: queryKeys.automationRail });
    },
  });
}

export function useCadencePause() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api<unknown>("/api/operator/cadence/pause", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.automationRail });
    },
  });
}

export function useCadenceResume() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api<unknown>("/api/operator/cadence/resume", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.automationRail });
    },
  });
}

export interface CallIntelligenceFlagPayload {
  id: string;
  flag: "accurate" | "partially_correct" | "wrong";
  note?: string;
}

export function useCallIntelligenceFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, flag, note }: CallIntelligenceFlagPayload) =>
      api<{ row: CallIntelligenceRow }>(
        `/api/operator/call-intelligence/${encodeURIComponent(id)}/flag`,
        {
          method: "POST",
          body: JSON.stringify({ flag, note }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["operator", "call-intelligence"] });
    },
  });
}

/**
 * Optimistic list removal: snapshot the current query, filter out the item
 * predicted to disappear, and return a rollback context. On error the caller
 * restores the snapshot; on settled it refetches to reconcile with the server.
 */
/**
 * Optimistic list removal inside a nested response shape. Given a getter
 * for the list and a setter that returns the mutated root, filter out the
 * item predicted to disappear and return a rollback snapshot.
 */
async function optimisticNestedListRemove<Root, Item>(
  qc: ReturnType<typeof useQueryClient>,
  queryKey: readonly unknown[],
  getList: (root: Root) => Item[] | undefined,
  setList: (root: Root, list: Item[]) => Root,
  predicate: (item: Item) => boolean,
): Promise<{ previous: unknown }> {
  await qc.cancelQueries({ queryKey });
  const previous = qc.getQueryData(queryKey);
  qc.setQueryData(queryKey, (current: unknown) => {
    if (current == null || typeof current !== "object") return current;
    const root = current as Root;
    const list = getList(root);
    if (!Array.isArray(list)) return current;
    return setList(root, list.filter((item) => !predicate(item)));
  });
  return { previous };
}

export function useCallbackComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { dispatchId: string }) =>
      api<unknown>("/api/operator/callback-sla/complete", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onMutate: (payload) =>
      optimisticNestedListRemove<
        { overdue?: Array<{ dispatchId: string }> },
        { dispatchId: string }
      >(
        qc,
        queryKeys.callbackSla,
        (root) => root.overdue,
        (root, overdue) => ({ ...root, overdue }),
        (item) => item.dispatchId === payload.dispatchId,
      ),
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(queryKeys.callbackSla, ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.callbackSla });
    },
  });
}

// Operator-driven sends — SMS + voicemail-drop from the SLA / queue
// drill-in. Both invalidate the work queue + SLA after settling so
// the caller sees the consequence.
export function useSendOperatorSms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { leadId: string; body: string }) =>
      api<{ dispatchId: string; status: string }>(
        `/api/operator/lead/${encodeURIComponent(payload.leadId)}/sms`,
        {
          method: "POST",
          body: JSON.stringify({ body: payload.body }),
        },
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.workQueue });
      void qc.invalidateQueries({ queryKey: queryKeys.automationRail });
      void qc.invalidateQueries({
        queryKey: ["operator", "reporting", "recovery"],
      });
    },
  });
}

export function useSendVoicemailDrop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { leadId: string }) =>
      api<{ dispatchId: string; status: string }>(
        `/api/operator/lead/${encodeURIComponent(payload.leadId)}/voicemail-drop`,
        { method: "POST" },
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.workQueue });
      void qc.invalidateQueries({ queryKey: queryKeys.automationRail });
    },
  });
}

export interface VoicemailStatus {
  configured: boolean;
  source?: string;
  configuredAt?: string;
  durationSec?: number | null;
}

export function useVoicemailStatus() {
  return useQuery({
    queryKey: ["operator", "voicemail-status"] as const,
    queryFn: () => api<VoicemailStatus>("/api/operator/voicemail/status"),
  });
}

export function useIssueVoicemailSetupLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { text?: boolean }) =>
      api<{
        url: string;
        token: string;
        expiresAt: string;
        smsStatus: "sent" | "failed" | "skipped";
      }>("/api/operator/voicemail/setup-link", {
        method: "POST",
        body: JSON.stringify(payload || {}),
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["operator", "voicemail-status"] });
    },
  });
}

export interface CallbackAssistResult {
  suggestedSms: string;
  talkingPoints: string[];
  source: "llm" | "fallback";
  modelId?: string;
  promptVersion?: string;
  reason?: string;
}

export function useCallbackAssist() {
  return useMutation({
    mutationFn: (payload: { dispatchId: string }) =>
      api<CallbackAssistResult>("/api/operator/callback-sla/assist", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
  });
}

export type CallbackOutcome =
  | "booked"
  | "rescheduled"
  | "reached"
  | "voicemail_left"
  | "no_answer"
  | "not_interested";

export function useCallbackOutcome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      dispatchId: string;
      outcome: CallbackOutcome;
      notes?: string;
    }) =>
      api<unknown>("/api/operator/callback-sla/outcome", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onMutate: (payload) =>
      optimisticNestedListRemove<
        { overdue?: Array<{ dispatchId: string }>; upcoming?: Array<{ dispatchId: string }> },
        { dispatchId: string }
      >(
        qc,
        queryKeys.callbackSla,
        (root) => [...(root.overdue ?? []), ...(root.upcoming ?? [])],
        (root, _) => {
          const remove = (list: Array<{ dispatchId: string }> | undefined) =>
            (list ?? []).filter((i) => i.dispatchId !== payload.dispatchId);
          return { ...root, overdue: remove(root.overdue), upcoming: remove(root.upcoming) };
        },
        (item) => item.dispatchId === payload.dispatchId,
      ),
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(queryKeys.callbackSla, ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.callbackSla });
    },
  });
}

export function useCallbackEscalate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { dispatchId: string }) =>
      api<unknown>("/api/operator/callback-sla/escalate", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onMutate: (payload) =>
      optimisticNestedListRemove<
        { overdue?: Array<{ dispatchId: string }> },
        { dispatchId: string }
      >(
        qc,
        queryKeys.callbackSla,
        (root) => root.overdue,
        (root, overdue) => ({ ...root, overdue }),
        (item) => item.dispatchId === payload.dispatchId,
      ),
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(queryKeys.callbackSla, ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.callbackSla });
    },
  });
}

export function usePaymentAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api<unknown>("/api/operator/payment/action", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.paymentReminders });
      void qc.invalidateQueries({ queryKey: queryKeys.agingReport });
    },
  });
}

export function useAttributionConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { matchId: string; confirmedBy?: string }) =>
      api<unknown>("/api/operator/attribution/confirm", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onMutate: (payload) =>
      optimisticNestedListRemove<
        { matches?: Array<{ id: string; status: string }> },
        { id: string; status: string }
      >(
        qc,
        queryKeys.revenueProof,
        (root) => root.matches,
        (root, matches) => ({ ...root, matches }),
        (item) => item.id === payload.matchId && item.status === "pending",
      ),
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(queryKeys.revenueProof, ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.revenueProof });
    },
  });
}

export function useAttributionReject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { matchId: string; reason?: string }) =>
      api<unknown>("/api/operator/attribution/reject", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onMutate: (payload) =>
      optimisticNestedListRemove<
        { matches?: Array<{ id: string; status: string }> },
        { id: string; status: string }
      >(
        qc,
        queryKeys.revenueProof,
        (root) => root.matches,
        (root, matches) => ({ ...root, matches }),
        (item) => item.id === payload.matchId && item.status === "pending",
      ),
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(queryKeys.revenueProof, ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.revenueProof });
    },
  });
}

/**
 * Recent reporting deliveries for the active tenant. Backed by the new
 * reporting module's `report_deliveries` table — there is no parallel
 * legacy log path. Returns empty when migrations 047/048/049 haven't run
 * yet (the endpoint catches the relation-missing error and returns []).
 */
export interface ReportingDelivery {
  id: number;
  categoryKey: string;
  scopeKind: string;
  scopeId: string | null;
  channel: string;
  recipient: string;
  sender: string;
  status: string;
  suppressionReason: string | null;
  idempotencyKey: string;
  payloadFingerprint: string;
  metadata: Record<string, unknown>;
  sentAt: string;
}

export interface ReportingDeliveriesResponse {
  deliveries: ReportingDelivery[];
  generatedAt: string;
  note?: string;
}

export function useReportingDeliveries(limit = 50) {
  return useQuery({
    queryKey: queryKeys.reportingDeliveries(limit),
    queryFn: () =>
      api<ReportingDeliveriesResponse>(
        `/api/operator/reporting/deliveries?limit=${limit}`,
      ),
  });
}

export interface OvernightDigestPreview {
  report: {
    id?: string;
    periodStart?: string;
    generatedAt?: string;
    metrics?: {
      afterHoursInteractions?: number;
      emergencyInteractions?: number;
      callbacksQueued?: number;
      messagesQueued?: number;
    };
    items?: Array<{
      customerName?: string;
      source?: string;
      status?: string;
      nextAction?: string;
      emergency?: boolean;
    }>;
  };
  generatedAt: string;
}

/**
 * Renders the overnight digest payload as the system would generate it
 * right now, without delivering. Used by the Preview button on
 * /reporting/deliveries so the owner can audit the morning brief before
 * letting the cadence land it in customer-facing inboxes.
 */
export function useOvernightDigestPreview() {
  return useMutation({
    mutationFn: () =>
      api<OvernightDigestPreview>(
        "/api/operator/reporting/overnight/preview",
        { method: "GET" },
      ),
  });
}

/**
 * Delivers the overnight digest now (operator-initiated). Same payload
 * the cadence sends; lands in the account owner's configured digest
 * recipient address. Invalidates the deliveries list on success.
 */
export function useDeliverOvernightDigest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean; delivery: Record<string, unknown> }>(
        "/api/operator/reporting/overnight/deliver",
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["operator", "reporting", "deliveries"] });
    },
  });
}

export interface RecoveryTrendPoint {
  date: string;
  touches: number;
  outcomes: number;
  recovered: number;
}

export interface RecoveryFunnelCounts {
  missedCalls: number;
  touchesSent: number;
  callbacksScheduled: number;
  booked: number;
  recoveredRevenue: number;
}

export interface RecoveryCadenceRow {
  templateKey: string;
  sends: number;
  replies: number;
  outcomes: number;
  won: number;
  recovered: number;
}

export interface RecoveryReceipt {
  customerName: string;
  amount: number;
  recoveredAt: string;
  viaTemplateKey: string | null;
}

export interface RecoverySummaryResponse {
  windowDays: number;
  since: string;
  generatedAt: string;
  trend: RecoveryTrendPoint[];
  funnel: RecoveryFunnelCounts;
  perCadence: RecoveryCadenceRow[];
  /** Top N highest-value Won outcomes in the window — used by the
   * Recovery Receipts list on /reporting/recovery to tell the story
   * of how the headline number was earned. */
  topRecoveries: RecoveryReceipt[];
  notProvisioned: boolean;
}

export function useReportingRecovery(windowDays = 30) {
  return useQuery({
    queryKey: queryKeys.reportingRecovery(windowDays),
    queryFn: () =>
      api<RecoverySummaryResponse>(
        `/api/operator/reporting/recovery?windowDays=${windowDays}`,
      ),
  });
}

/* ── Wave 4b — Call routing dashboard ── */

export type CaptureLayerKey =
  | "layer1_dial"
  | "layer2_ivr_urgent"
  | "layer2_ivr_sms"
  | "layer2_ivr_vm"
  | "layer3_sms_auto"
  | "fallthrough_vm";

export interface CallRoutingLayerStats {
  layer: CaptureLayerKey;
  label: string;
  count: number;
  recovered: number;
  inFlight: number;
  lost: number;
  /** 0..1 */
  recoveryRate: number;
}

export interface CallRoutingSummaryResponse {
  windowDays: number;
  totalVoiceLeads: number;
  unattributed: number;
  layers: CallRoutingLayerStats[];
  /** 0..1 */
  overallRecoveryRate: number;
  generatedAt: string;
}

export function useCallRoutingSummary(windowDays = 30) {
  return useQuery({
    queryKey: ["operator", "reporting", "call-routing", windowDays] as const,
    queryFn: () =>
      api<CallRoutingSummaryResponse>(
        `/api/operator/reporting/call-routing?windowDays=${windowDays}`,
      ),
  });
}

/**
 * Tenant-audience reporting categories joined with the active tenant's
 * subscription rows. Used by the Notifications page in PlatformPage to
 * let owners enable/disable each category.
 */
export interface NotificationCategory {
  categoryKey: string;
  displayName: string;
  description: string;
  defaultCadence: Record<string, unknown>;
  defaultChannels: string[];
  senderAddress: string;
}
export interface NotificationSubscription {
  categoryKey: string;
  enabled: boolean;
  cadenceOverride: Record<string, unknown> | null;
  recipientEmails: string[];
  recipientPhones: string[];
  channels: string[] | null;
  frequencyCap: number;
  quietHours: string[] | null;
}
export interface NotificationsResponse {
  categories: NotificationCategory[];
  subscriptions: NotificationSubscription[];
  generatedAt: string;
}

export function useNotifications() {
  return useQuery({
    queryKey: queryKeys.notifications,
    queryFn: () => api<NotificationsResponse>("/api/operator/notifications"),
  });
}

export function useToggleNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { categoryKey: string; enabled: boolean }) =>
      api<{ ok: boolean; categoryKey: string; enabled: boolean }>(
        "/api/operator/notifications/toggle",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

export function useAccountMembers() {
  return useQuery({
    queryKey: queryKeys.accountMembers,
    queryFn: () => api<unknown>("/api/account/members"),
  });
}

export interface AccountSubscriptionView {
  status:
    | "trialing"
    | "active"
    | "incomplete"
    | "incomplete_expired"
    | "past_due"
    | "canceled"
    | "unpaid"
    | "paused";
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  trialStart: string | null;
  trialEnd: string | null;
  stripePriceId: string;
  createdAt: string;
  updatedAt: string;
}

export interface AccountUsageResponse {
  periodStart: string;
  periodEnd: string;
  fieldedContacts: number;
  generatedAt: string;
  note?: string;
}

/**
 * Current billing-period fielded-contact count for this account.
 * Backs the usage bar on /billing — the "X of 250 included" reading
 * the audit asked for so an owner can answer "where am I against my
 * plan" without leaving the page.
 */
export function useAccountUsage() {
  return useQuery({
    queryKey: ["operator", "usage"],
    queryFn: () => api<AccountUsageResponse>("/api/operator/usage"),
  });
}

export function useAccountSubscription() {
  return useQuery({
    queryKey: queryKeys.accountSubscription,
    queryFn: () =>
      api<{ subscription: AccountSubscriptionView | null }>(
        "/api/account/subscription",
      ),
  });
}

export function useBillingPortalSession() {
  return useMutation({
    mutationFn: () =>
      api<{ url: string; sessionId: string }>("/api/billing/portal", {
        method: "POST",
      }),
  });
}

export interface BillingPlan {
  key: string;
  label: string;
  description: string;
  price: number;
  interval: string;
  priceId: string;
}

export interface BillingConfig {
  provider: string;
  enabled: boolean;
  publishableKey: string;
  plans: BillingPlan[];
}

export function useBillingConfig() {
  return useQuery({
    queryKey: ["billing", "config"] as const,
    queryFn: () => api<BillingConfig>("/api/billing/config"),
  });
}

export function useAccountSettingsUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (account: Record<string, unknown>) =>
      api<unknown>("/api/account/settings", {
        method: "PUT",
        body: JSON.stringify({ account }),
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
    },
  });
}

export function useBillingCheckout() {
  return useMutation({
    mutationFn: (payload: { priceId: string }) =>
      api<{ url: string; sessionId: string }>("/api/billing/checkout", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
  });
}

export interface AuditEventView {
  id: string;
  eventType: string;
  decision: string;
  userId: string | null;
  accountId: string | null;
  actorUserId: string | null;
  reason: string | null;
  ip: string | null;
  userAgent: string | null;
  requestId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export function useAccountAuditTrail(
  eventType: string | undefined,
  limit: number,
  phoneKey?: string,
) {
  const normalizedPhone = (phoneKey || "").replace(/\D/g, "");
  return useQuery({
    queryKey: queryKeys.accountAuditTrail(eventType, limit, normalizedPhone),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (eventType) qs.set("eventType", eventType);
      qs.set("limit", String(limit));
      if (normalizedPhone) qs.set("phoneKey", normalizedPhone);
      return api<{ events: AuditEventView[]; total: number }>(
        `/api/account/audit-trail?${qs.toString()}`,
      );
    },
  });
}

export function useSelfSessions() {
  return useQuery({
    queryKey: queryKeys.selfSessions,
    queryFn: () => api<unknown>("/api/auth/sessions"),
  });
}

function invalidateAccountAndMembers(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: queryKeys.app });
  void qc.invalidateQueries({ queryKey: queryKeys.accountMembers });
}

export function useMemberCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      displayName: string;
      email: string;
      username: string;
      role: string;
      temporaryPassword?: string;
    }) =>
      api<unknown>("/api/account/members", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => invalidateAccountAndMembers(qc),
  });
}

export function useMemberUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      ...body
    }: {
      userId: string;
      displayName: string;
      email: string;
      role: string;
      membershipStatus: string;
    }) =>
      api<unknown>(`/api/account/members/${encodeURIComponent(userId)}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateAccountAndMembers(qc),
  });
}

export function useMemberDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<unknown>(`/api/account/members/${encodeURIComponent(userId)}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidateAccountAndMembers(qc),
  });
}

export function useMemberResetPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      temporaryPassword,
    }: {
      userId: string;
      temporaryPassword?: string;
    }) =>
      api<{ temporaryPassword: string; revokedSessionCount: number }>(
        `/api/account/members/${encodeURIComponent(userId)}/reset-password`,
        {
          method: "POST",
          body: JSON.stringify({ temporaryPassword }),
        },
      ),
    onSuccess: () => invalidateAccountAndMembers(qc),
  });
}

export function useMemberRevokeSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<{ revokedSessionCount: number }>(
        `/api/account/members/${encodeURIComponent(userId)}/revoke-sessions`,
        { method: "POST" },
      ),
    onSuccess: () => invalidateAccountAndMembers(qc),
  });
}

/**
 * SMS the new member their invite link + (optional) temp password via
 * the configured Twilio number. Closes the loop on the audit's Atlas-8
 * finding so the owner doesn't have to alt-tab to their personal phone.
 */
export function useMemberSmsInvite() {
  return useMutation({
    mutationFn: (payload: {
      userId: string;
      phone: string;
      temporaryPassword?: string;
    }) =>
      api<{ ok: boolean; status: string; dispatchId: string; error?: string }>(
        `/api/account/members/${encodeURIComponent(payload.userId)}/sms-invite`,
        {
          method: "POST",
          body: JSON.stringify({
            phone: payload.phone,
            ...(payload.temporaryPassword
              ? { temporaryPassword: payload.temporaryPassword }
              : {}),
          }),
        },
      ),
  });
}

export function useProfileUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { displayName: string; email: string }) =>
      api<unknown>("/api/auth/profile", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.session });
      void qc.invalidateQueries({ queryKey: queryKeys.app });
    },
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      currentPassword: string;
      newPassword: string;
    }) =>
      api<unknown>("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useChangeUsername() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { currentPassword: string; newUsername: string }) =>
      api<unknown>("/api/auth/change-username", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useRevokeOtherSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ revokedCount: number }>("/api/auth/sessions/revoke-others", {
        method: "POST",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.selfSessions });
    },
  });
}

export function useMfaSetup() {
  return useMutation({
    mutationFn: (payload: { currentPassword: string }) =>
      api<{ secret: string; otpauthUri: string }>("/api/auth/mfa/setup", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
  });
}

export function useMfaVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { code: string }) =>
      api<{ recoveryCodes: string[] }>("/api/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useMfaDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      currentPassword: string;
      totpCode?: string;
      recoveryCode?: string;
    }) =>
      api<unknown>("/api/auth/mfa/disable", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useResendVerification() {
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean; alreadyVerified?: boolean }>(
        "/api/auth/resend-verification",
        { method: "POST" },
      ),
  });
}

export function useOnboardingStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { step: string; data?: unknown }) =>
      api<unknown>("/api/account/onboarding/step", {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
    },
  });
}

export function useOnboardingComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { skipped: boolean }) =>
      api<unknown>("/api/account/onboarding/complete", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
    },
  });
}

// Sandbox-only: replace this account's data with the seeded demo set.
// Backend gates on the account being is_sandbox=true with no went_live_at,
// and the requesting user being an owner/operator on the account. After
// go-live this endpoint returns 403 — never wipe a customer's real data.
export function useResetDemoData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<unknown>("/api/reset?confirm=true", { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries();
    },
  });
}

// ── Data imports (Phase 2 unified imports pipeline) ────────────────────

export type ImportVendor =
  | "servicetitan"
  | "jobber"
  | "housecallpro"
  | "servicefusion"
  | "generic";

export type ImportTransport = "upload" | "webhook";

export interface ImportSourceView {
  id: string;
  displayName: string;
  vendor: ImportVendor;
  transport: ImportTransport;
  cadence: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  lastRunAt: string | null;
  /** Returned ONCE on creation; never on subsequent reads. */
  webhookSecret?: string;
}

export interface ImportRunView {
  id: string;
  sourceId: string | null;
  vendor: ImportVendor;
  transport: ImportTransport;
  entityType: string;
  status: "queued" | "running" | "succeeded" | "failed" | "partial";
  rowsTotal: number;
  rowsCreated: number;
  rowsUpdated: number;
  rowsErrored: number;
  errors: Array<{ rowIndex: number; message: string }>;
  triggeredBy: string;
  payloadBytes: number;
  startedAt: string;
  finishedAt: string | null;
}

export function useImportSources() {
  return useQuery({
    queryKey: queryKeys.importSources,
    queryFn: () =>
      api<{ sources: ImportSourceView[] }>("/api/imports/sources"),
  });
}

export function useImportRuns(limit = 25) {
  return useQuery({
    queryKey: queryKeys.importRuns(limit),
    queryFn: () =>
      api<{ runs: ImportRunView[] }>(`/api/imports/runs?limit=${limit}`),
  });
}

export function useCreateImportSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      displayName: string;
      vendor: ImportVendor;
      transport: ImportTransport;
      cadence?: string;
    }) =>
      api<{ source: ImportSourceView }>("/api/imports/sources", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.importSources });
    },
  });
}

export function useDeleteImportSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ removed: boolean }>(`/api/imports/sources/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.importSources });
    },
  });
}

export function useUploadImportCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      csvText: string;
      vendor: ImportVendor;
      sourceId?: string;
    }) => {
      const qs = new URLSearchParams();
      qs.set("vendor", payload.vendor);
      if (payload.sourceId) qs.set("sourceId", payload.sourceId);
      return api<{ run: ImportRunView }>(`/api/imports/upload?${qs.toString()}`, {
        method: "POST",
        body: payload.csvText,
        headers: { "Content-Type": "text/csv" },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.importSources });
      void qc.invalidateQueries({ queryKey: ["account", "imports", "runs"] });
      void qc.invalidateQueries({ queryKey: queryKeys.app });
    },
  });
}

/* ── Contact write-path (Wave 5b foundation) ── */

export type ContactChangeReason =
  | "customer_requested"
  | "operator_verified"
  | "dnc_compliance"
  | "other";

export interface ContactPatch {
  preferredChannel?: PreferredChannelValue;
  smsOptOut?: boolean;
  emailOptOut?: boolean;
  dncFlag?: boolean;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  company?: string;
  email?: string;
  notes?: string;
  changeReason: string;
}

export interface ContactHistoryRow {
  id: number;
  fieldName: string;
  oldValue: string;
  newValue: string;
  changedBy: string;
  changeReason: string;
  ip: string;
  userAgent: string;
  changedAt: string;
}

export interface ContactHistoryResponse {
  phoneKey: string;
  history: ContactHistoryRow[];
  total: number;
}

/**
 * Update a contact's preferences / identity with the audited write
 * path. The PATCH route (operator.ts) requires `changeReason` for
 * sensitive fields (preferred_channel, sms_opt_out, email_opt_out,
 * dnc_flag) — the DB trigger raises otherwise. UI should force the
 * reason dropdown before allowing those toggles.
 */
export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { phoneKey: string; patch: ContactPatch }) =>
      api<{ contact: Record<string, unknown> }>(
        `/api/operator/contacts/${encodeURIComponent(payload.phoneKey)}`,
        { method: "PATCH", body: JSON.stringify(payload.patch) },
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: queryKeys.app });
      void qc.invalidateQueries({ queryKey: queryKeys.actionStack });
      void qc.invalidateQueries({
        queryKey: queryKeys.contactHistory(variables.phoneKey, 50),
      });
    },
  });
}

export function useContactHistory(phoneKey: string | null, limit = 50) {
  return useQuery({
    queryKey: queryKeys.contactHistory(phoneKey, limit),
    queryFn: () =>
      api<ContactHistoryResponse>(
        `/api/operator/contacts/${encodeURIComponent(phoneKey ?? "")}/history?limit=${limit}`,
      ),
    enabled: Boolean(phoneKey),
  });
}

// ─── Platform / system console ────────────────────────────────────────

export interface TenancyAccountEntry {
  id: string;
  kind: string;
  source: string;
  sourceLeadId: string;
  createdAt: string;
  updatedAt: string;
  account: {
    id: string;
    companyName: string;
    trade?: string;
    serviceArea?: string;
    businessPhone?: string;
    ownerEmail?: string;
    website?: string;
    activation?: Record<string, unknown>;
    onboarding?: Record<string, unknown>;
    twilio?: Record<string, unknown>;
  };
}

export interface TenantOverviewRow {
  accountId: string;
  accountName: string;
  kind: string;
  status: string;
  automationPaused: boolean;
  metrics: Record<string, number>;
  lastActivityAt: string;
  userCount: number;
}

export interface AttentionItem {
  id: string;
  accountId: string;
  accountName: string;
  targetType: string;
  targetId: string;
  workspace: string;
  severity: string;
  status: string;
  title: string;
  summary: string;
  createdAt: string;
  recommendedAction: { entity: string; action: string; label: string } | null;
}

export interface QueueStatsView {
  total?: number;
  byStatus?: Record<string, number>;
  byKind?: Record<string, number>;
  oldestPending?: string | null;
  oldestDeadLetter?: string | null;
  [key: string]: unknown;
}

export interface QueueDeadLetter {
  id: string;
  kind?: string;
  failureReason?: string;
  attempts?: number;
  failedAt?: string;
  [key: string]: unknown;
}

export interface MrrPlanBreakdownRow {
  priceId: string;
  label: string;
  subs: number;
  monthlyCents: number;
}

export interface MrrSnapshotView {
  mrrCents: number;
  activeSubs: number;
  trialingSubs: number;
  pastDueSubs: number;
  planBreakdown: MrrPlanBreakdownRow[];
  unknownPriceIds: string[];
}

export interface RecentSignupRow {
  accountId: string;
  companyName: string;
  trade: string;
  signedUpAt: string;
  ownerEmail: string;
  planLabel: string | null;
  subStatus: string | null;
}

export interface PlatformAttentionItem {
  id: string;
  severity: "high" | "medium" | "low";
  category:
    | "payment_failure"
    | "trial_expiring"
    | "churn_risk"
    | "a2p_stuck";
  title: string;
  summary: string;
  accountId: string;
  accountName: string;
  createdAt: string;
}

export interface DispatchSuccessWindowView {
  windowDays: number;
  sent: number;
  failed: number;
  rate: string;
}

export interface SystemOverviewResponse {
  summary: Record<string, unknown>;
  tenants: TenantOverviewRow[];
  // attentionItems used to be operator-flavored intervention items;
  // platform-relevant rules (payment_failure / trial_expiring /
  // churn_risk / a2p_stuck) now ride the same field.
  attentionItems: PlatformAttentionItem[];
  queue: { stats: QueueStatsView; deadLetters: QueueDeadLetter[] };
  recentAudit: AuditEventView[];
  mrr: MrrSnapshotView;
  recentSignups: RecentSignupRow[];
  dispatchSuccess: DispatchSuccessWindowView;
}

export function useSystemOverview() {
  return useQuery({
    queryKey: ["system", "overview"] as const,
    queryFn: () => api<SystemOverviewResponse>("/api/system/overview"),
    refetchInterval: 30_000,
  });
}

export interface SubscriptionRowView {
  accountId: string;
  accountName: string;
  ownerEmail: string;
  status: string;
  planLabel: string;
  monthlyCents: number;
  trialEnd: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
}

export interface SystemBillingResponse {
  mrr: MrrSnapshotView;
  subscriptions: SubscriptionRowView[];
}

export function useSystemBilling() {
  return useQuery({
    queryKey: ["system", "billing"] as const,
    queryFn: () => api<SystemBillingResponse>("/api/system/billing"),
    refetchInterval: 60_000,
  });
}

export interface SystemSignupsResponse {
  days: number;
  limit: number;
  signups: RecentSignupRow[];
}

export function useSystemSignups(params: { days?: number; limit?: number } = {}) {
  const days = params.days ?? 30;
  const limit = params.limit ?? 100;
  return useQuery({
    queryKey: ["system", "signups", days, limit] as const,
    queryFn: () =>
      api<SystemSignupsResponse>(
        `/api/system/signups?days=${days}&limit=${limit}`,
      ),
    refetchInterval: 60_000,
  });
}

export interface PerTenantDispatchRowView {
  accountId: string;
  accountName: string;
  sent: number;
  failed: number;
  rate: string;
}

export interface SystemDispatchResponse {
  windowDays: number;
  rows: PerTenantDispatchRowView[];
}

export function useSystemDispatch(params: { windowDays?: number } = {}) {
  const windowDays = params.windowDays ?? 7;
  return useQuery({
    queryKey: ["system", "dispatch", windowDays] as const,
    queryFn: () =>
      api<SystemDispatchResponse>(
        `/api/system/dispatch?windowDays=${windowDays}`,
      ),
    refetchInterval: 30_000,
  });
}

export function usePlatformAuditLog(filters: {
  userId?: string;
  accountId?: string;
  eventType?: string;
  decision?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["system", "audit", filters] as const,
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filters.userId) qs.set("userId", filters.userId);
      if (filters.accountId) qs.set("accountId", filters.accountId);
      if (filters.eventType) qs.set("eventType", filters.eventType);
      if (filters.decision) qs.set("decision", filters.decision);
      qs.set("limit", String(filters.limit ?? 100));
      return api<{ events: AuditEventView[]; total: number }>(
        `/api/admin/audit-log?${qs.toString()}`,
      );
    },
  });
}

export function usePlatformDashboard() {
  return useQuery({
    queryKey: ["system", "dashboard"] as const,
    queryFn: () =>
      api<{
        summary: Record<string, unknown>;
        accounts: TenantOverviewRow[];
        interventionItems: AttentionItem[];
        users: Array<{
          userId: string;
          displayName: string;
          email: string;
          username: string;
          isPlatformAdmin: boolean;
          status: string;
          accounts: Array<{ accountId: string; role: string; status: string }>;
          lastLoginAt: string;
          source: string;
        }>;
      }>("/api/admin/platform-dashboard"),
  });
}

export function useQueueStats() {
  return useQuery({
    queryKey: ["system", "queue-stats"] as const,
    queryFn: () =>
      api<{ stats: QueueStatsView; deadLetters: QueueDeadLetter[] }>(
        "/api/admin/queue-stats",
      ),
    refetchInterval: 15_000,
  });
}

export function useCspViolations() {
  return useQuery({
    queryKey: ["system", "csp-violations"] as const,
    queryFn: () =>
      api<{
        violations: Array<{ directive: string; blockedUri: string; count: number }>;
        summary: { groupCount: number; totalCount: number; windowHours: number };
      }>("/api/admin/csp-violations"),
  });
}

export function useOnboardingPending(days = 30) {
  return useQuery({
    queryKey: ["system", "onboarding-pending", days] as const,
    queryFn: () =>
      api<{ days: number; items: Array<Record<string, unknown>> }>(
        `/api/admin/onboarding/pending?days=${days}`,
      ),
  });
}

export function useTenantLifecycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      accountId: string;
      action: "archive" | "restore" | "suspend";
    }) =>
      api<{ ok: boolean; account: { id: string; status: string } }>(
        `/api/admin/accounts/${encodeURIComponent(payload.accountId)}/${payload.action}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["system"] });
    },
  });
}

export function useSelectAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { accountId: string; reason?: string }) =>
      api<{ ok: boolean; accountId: string; viewAs?: boolean }>(
        "/api/accounts/select",
        { method: "POST", body: JSON.stringify(payload) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries();
    },
  });
}

export function useExitViewAs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean; accountId: string }>(
        "/api/accounts/exit-view-as",
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries();
    },
  });
}

export function useGrantPlatformRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      userId: string;
      role: "platform_owner" | "platform_support";
      reason?: string;
    }) =>
      api<{ ok: boolean; user: Record<string, unknown> }>(
        `/api/admin/users/${encodeURIComponent(payload.userId)}/platform-role`,
        {
          method: "POST",
          body: JSON.stringify({ role: payload.role, reason: payload.reason }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["system"] });
    },
  });
}

export function useRevokePlatformRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<{ ok: boolean; user: Record<string, unknown> }>(
        `/api/admin/users/${encodeURIComponent(userId)}/platform-role`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["system"] });
    },
  });
}

// ── Messaging compliance — A2P Brand / Campaign + TFV (per-tenant) ────

export interface A2pBrandView {
  accountId?: string;
  exists?: boolean;
  legalBusinessName?: string;
  displayName?: string;
  businessType?: string;
  entityType?: string;
  ein?: string;
  einIssuingCountry?: string;
  altBusinessIdType?: string;
  altBusinessId?: string;
  vertical?: string;
  businessContactFirstName?: string;
  businessContactLastName?: string;
  businessContactEmail?: string;
  businessContactPhone?: string;
  businessContactJobTitle?: string;
  businessContactJobPosition?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
  websiteUrl?: string;
  socialMediaUrl?: string;
  brandType?: "STANDARD" | "STARTER" | "SOLE_PROPRIETOR";
  customerProfileSid?: string | null;
  endUserSid?: string | null;
  addressSid?: string | null;
  trustProductSid?: string | null;
  brandSid?: string | null;
  customerProfileStatus?: string;
  trustProductStatus?: string;
  brandStatus?: string;
  brandFailureReason?: string;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  lastPolledAt?: string | null;
  updatedAt?: string;
}

export interface A2pCampaignView {
  id?: number;
  accountId?: string;
  exists?: boolean;
  campaignSlug?: string;
  brandSid?: string | null;
  messagingServiceSid?: string | null;
  usAppToPersonUsecase?: string;
  description?: string;
  messageSamples?: string[];
  messageFlow?: string;
  privacyPolicyUrl?: string;
  termsAndConditionsUrl?: string;
  optInKeywords?: string;
  optInMessage?: string;
  optOutKeywords?: string;
  optOutMessage?: string;
  helpKeywords?: string;
  helpMessage?: string;
  hasEmbeddedLinks?: boolean;
  hasEmbeddedPhone?: boolean;
  subscriberOptIn?: boolean;
  ageGated?: boolean;
  directLending?: boolean;
  numberPool?: boolean;
  campaignSid?: string | null;
  campaignStatus?: string;
  campaignFailureReason?: string;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  lastPolledAt?: string | null;
  updatedAt?: string;
}

export interface TfvView {
  id?: number;
  accountId?: string;
  exists?: boolean;
  tollfreePhoneNumberSid?: string;
  businessName?: string;
  businessWebsite?: string;
  businessStreetAddress?: string;
  businessStreetAddress2?: string;
  businessCity?: string;
  businessStateProvinceRegion?: string;
  businessPostalCode?: string;
  businessCountry?: string;
  businessContactFirstName?: string;
  businessContactLastName?: string;
  businessContactEmail?: string;
  businessContactPhone?: string;
  notificationEmail?: string;
  useCaseCategories?: string[];
  useCaseSummary?: string;
  productionMessageSample?: string;
  optInImageUrls?: string[];
  optInType?: string;
  messageVolume?: string;
  verificationSid?: string | null;
  verificationStatus?: string;
  rejectionReason?: string;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  lastPolledAt?: string | null;
  updatedAt?: string;
}

export interface TfvListResponse {
  rows: TfvView[];
}

const messagingComplianceKeys = {
  brand: ["account", "messaging-compliance", "brand"] as const,
  campaign: (slug: string) =>
    ["account", "messaging-compliance", "campaign", slug] as const,
  tfvList: ["account", "messaging-compliance", "tfv", "list"] as const,
  tfv: (sid: string) =>
    ["account", "messaging-compliance", "tfv", sid] as const,
} as const;

export function useA2pBrand() {
  return useQuery({
    queryKey: messagingComplianceKeys.brand,
    queryFn: () => api<A2pBrandView>("/api/account/a2p/brand"),
  });
}

export function useA2pCampaign(slug = "primary") {
  return useQuery({
    queryKey: messagingComplianceKeys.campaign(slug),
    queryFn: () =>
      api<A2pCampaignView>(
        `/api/account/a2p/campaign?slug=${encodeURIComponent(slug)}`,
      ),
  });
}

export function useTfvList() {
  return useQuery({
    queryKey: messagingComplianceKeys.tfvList,
    queryFn: () => api<TfvListResponse>("/api/account/tfv"),
  });
}

export function useUpdateA2pBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<A2pBrandView>) =>
      api<A2pBrandView>("/api/account/a2p/brand", {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: messagingComplianceKeys.brand });
    },
  });
}

export function useUpdateA2pCampaign(slug = "primary") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<A2pCampaignView>) =>
      api<A2pCampaignView>("/api/account/a2p/campaign", {
        method: "PUT",
        body: JSON.stringify({ campaignSlug: slug, ...payload }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: messagingComplianceKeys.campaign(slug),
      });
    },
  });
}

export function useUpdateTfv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<TfvView> & { tollfreePhoneNumberSid: string }) =>
      api<TfvView>("/api/account/tfv", {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: messagingComplianceKeys.tfvList,
      });
    },
  });
}
